import { useFormContext } from 'react-hook-form';
import { DateTimePicker } from '@knack/asterisk-react';

import { type DateTimeFieldFormat } from '@/types/schema/fields/DateTimeField';
import { type KnackField } from '@/types/schema/KnackField';
import { type FormViewTimerInput } from '@/types/schema/views/form/Timer';
import { type FormView } from '@/types/schema/views/FormView';
import { useFormRecordValues } from '@/hooks/useFormRecordValues';
import { FormErrorMessage } from '@/components/errors/FormErrorMessage';
import { getKnackDatePickerFormatFromField } from '@/components/knack-date-picker/transformers/DateTimeFormatToLuxonFormat';
import { JsDateToKnackDateTransformer } from '@/components/knack-date-picker/transformers/JsDateToKnackDateTransformer';
import { KnackDateToJsDateTransformer } from '@/components/knack-date-picker/transformers/KnackDateToJsDateTransformer';
import { useViewContext } from '@/components/views/ViewContext';

export function TimerInput({
  input,
  isReadOnly,
  field
}: {
  input: FormViewTimerInput;
  isReadOnly?: boolean;
  field: KnackField;
}) {
  const {
    getValues,
    setValue,
    formState: { errors }
  } = useFormContext();

  // To render the value in read-only mode, we need to get the record values from the form context
  const { view } = useViewContext<FormView>();
  const { recordValues } = useFormRecordValues(view);

  const datePickerValue = getValues(input.field.key)
    ? {
        from: KnackDateToJsDateTransformer(
          getValues(input.field.key)?.from,
          field.format as DateTimeFieldFormat
        ),
        to: KnackDateToJsDateTransformer(
          getValues(input.field.key)?.to,
          field.format as DateTimeFieldFormat
        )
      }
    : undefined;

  return (
    <>
      {isReadOnly && (
        <div className="text-muted-foreground mb-2 text-sm">
          <p className="truncate">{recordValues?.values?.[input.field.key]}</p>
        </div>
      )}
      {!isReadOnly && (
        <>
          <DateTimePicker
            mode="range"
            intent={errors[input.field.key] ? 'destructive' : 'default'}
            format={getKnackDatePickerFormatFromField(field.format as DateTimeFieldFormat)}
            value={datePickerValue}
            onChange={(newValue) => {
              if (!newValue?.from && !newValue?.to) {
                setValue(input.field.key, { times: [] });
                return;
              }

              const fromValue = JsDateToKnackDateTransformer(
                newValue?.from,
                field.format as DateTimeFieldFormat
              );
              const toValue = JsDateToKnackDateTransformer(
                newValue?.to,
                field.format as DateTimeFieldFormat
              );

              // Remove `iso_timestamp` properties from the transformed value to avoid including them in the schema
              if (fromValue.iso_timestamp) {
                delete fromValue.iso_timestamp;
              }
              if (toValue.iso_timestamp) {
                delete toValue.iso_timestamp;
              }

              setValue(input.field.key, {
                from: fromValue,
                to: toValue
              });
            }}
          />

          <FormErrorMessage
            className="mt-1"
            errors={errors}
            name={`${input.field.key}.times.0.from`}
          />
          <FormErrorMessage
            className="mt-1"
            errors={errors}
            name={`${input.field.key}.times.0.to`}
          />
        </>
      )}
      <FormErrorMessage errors={errors} name={input.field.key} />
    </>
  );
}
